import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Loader2, Play, RefreshCw, Monitor, CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { supabase } from "../../../lib/supabase";

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

const SCRAPERS = [
  { id: '5pct', label: 'Kepemilikan 5%', description: 'Laporan kepemilikan saham ≥5%', color: 'bg-blue-600 hover:bg-blue-700' },
  { id: '1pct', label: 'Kepemilikan 1%', description: 'Laporan kepemilikan saham ≥1%', color: 'bg-violet-600 hover:bg-violet-700' },
  { id: 'hcl', label: 'HCL (Konsentrasi)', description: 'Kepemilikan saham terkonsentrasi tinggi', color: 'bg-amber-600 hover:bg-amber-700' },
  { id: 'ff', label: 'Free Float (FF)', description: 'Laporan free float (quarterly)', color: 'bg-emerald-600 hover:bg-emerald-700' },
  { id: 'perubahan', label: 'Perubahan Kepemilikan', description: 'Laporan perubahan kepemilikan KSEI', color: 'bg-rose-600 hover:bg-rose-700' },
] as const;

type ScraperId = typeof SCRAPERS[number]['id'];
type RunStatus = 'idle' | 'triggering' | 'queued' | 'starting_vm' | 'running' | 'success' | 'failure';

interface ScraperState {
  status: RunStatus;
  runId?: string;
  triggeredAt?: Date;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

export function PythonScraperControl() {
  const { showToast } = useToast();
  const [scraperStates, setScraperStates] = useState<Record<ScraperId, ScraperState>>({
    '5pct': { status: 'idle' },
    '1pct': { status: 'idle' },
    'hcl': { status: 'idle' },
    'ff': { status: 'idle' },
    'perubahan': { status: 'idle' },
  });
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/developer/python-scrapers/runs?per_page=10`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      if (data.ok) setRecentRuns(data.data || []);
    } catch (e) {
      console.error('Failed to fetch runs:', e);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Poll status for in-progress scrapers
  useEffect(() => {
    const activeIds = (Object.keys(scraperStates) as ScraperId[]).filter(id =>
      ['triggering', 'queued', 'starting_vm', 'running'].includes(scraperStates[id].status)
    );
    if (activeIds.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_URL}/api/developer/python-scrapers/runs?per_page=10`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        const runs: any[] = data.data || [];
        setRecentRuns(runs);

        // Match runs to active scrapers
        setScraperStates(prev => {
          const next = { ...prev };
          (Object.keys(next) as ScraperId[]).forEach(id => {
            const state = next[id];
            if (!['triggering', 'queued', 'starting_vm', 'running'].includes(state.status)) return;

            // If we have a runId, match it
            if (state.runId) {
              const run = runs.find(r => r.id === state.runId);
              if (run) {
                next[id] = { ...state, status: run.status as RunStatus };
              }
            }
          });
          return next;
        });
      } catch (e) {
        console.error('Poll error:', e);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [scraperStates]);

  const triggerScraper = async (id: ScraperId) => {
    setScraperStates(prev => ({ ...prev, [id]: { status: 'triggering', triggeredAt: new Date() } }));

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/developer/python-scrapers/trigger`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ job: id }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.ok) throw new Error(data.message || 'Trigger failed');

      const runId = data.data?.runId;
      setScraperStates(prev => ({ ...prev, [id]: { status: 'queued', runId, triggeredAt: prev[id].triggeredAt } }));
      showToast({ type: 'success', title: 'Triggered', message: `Scraper '${id}' queued on VM` });

      // Refresh runs after a short delay
      setTimeout(fetchRuns, 2000);
    } catch (error: any) {
      setScraperStates(prev => ({ ...prev, [id]: { status: 'idle' } }));
      showToast({ type: 'error', title: 'Error', message: error.message || 'Failed to trigger scraper' });
    }
  };

  const getStatusBadge = (status: RunStatus) => {
    switch (status) {
      case 'idle':
        return null;
      case 'triggering':
        return <Badge variant="secondary" className="text-xs gap-1"><Loader2 className="h-3 w-3 animate-spin" />Triggering...</Badge>;
      case 'queued':
        return <Badge variant="secondary" className="text-xs gap-1"><Clock className="h-3 w-3" />Queued</Badge>;
      case 'starting_vm':
        return <Badge className="text-xs gap-1 bg-amber-500 text-white"><Loader2 className="h-3 w-3 animate-spin" />Starting VM...</Badge>;
      case 'running':
        return <Badge className="text-xs gap-1 bg-blue-500 text-white"><Loader2 className="h-3 w-3 animate-spin" />Running</Badge>;
      case 'success':
        return <Badge className="text-xs gap-1 bg-emerald-500 text-white"><CheckCircle2 className="h-3 w-3" />Done ✓ Success</Badge>;
      case 'failure':
        return <Badge variant="destructive" className="text-xs gap-1"><XCircle className="h-3 w-3" />Done ✗ Error</Badge>;
    }
  };

  const isActive = (status: RunStatus) => ['triggering', 'queued', 'starting_vm', 'running'].includes(status);

  return (
    <Card className="w-full border-zinc-200 dark:border-zinc-800">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-xl font-bold">
            <Monitor className="h-5 w-5 text-orange-500" />
            Python Scrapers (Direct SSH)
          </CardTitle>
          <CardDescription>
            Trigger dan monitor 5 scraper kepemilikan saham langsung ke VM
          </CardDescription>
        </div>
        <Button onClick={fetchRuns} size="sm" variant="outline" disabled={loadingRuns}>
          <RefreshCw className={`h-4 w-4 ${loadingRuns ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Trigger Buttons Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {SCRAPERS.map(scraper => {
            const state = scraperStates[scraper.id];
            const active = isActive(state.status);
            const done = state.status === 'success' || state.status === 'failure' || state.status === 'cancelled';

            return (
              <div key={scraper.id} className="flex flex-col gap-2 p-3 border rounded-lg bg-zinc-50/50 dark:bg-zinc-900/50">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{scraper.label}</span>
                  <span className="text-[10px] text-zinc-400 leading-tight">{scraper.description}</span>
                </div>

                <Button
                  onClick={() => triggerScraper(scraper.id)}
                  disabled={active}
                  size="sm"
                  className={`w-full text-white text-xs ${scraper.color}`}
                >
                  {active
                    ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Running...</>
                    : <><Play className="h-3 w-3 mr-1" />Run</>
                  }
                </Button>

                {state.status !== 'idle' && (
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(state.status)}
                    {done && state.runUrl && (
                      <a
                        href={state.runUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-indigo-500 hover:underline truncate"
                      >
                        View logs ↗
                      </a>
                    )}
                    {done && (
                      <button
                        onClick={() => setScraperStates(prev => ({ ...prev, [scraper.id]: { status: 'idle' } }))}
                        className="text-[10px] text-zinc-400 hover:text-zinc-600 text-left"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Recent Runs List */}
        <div className="space-y-2">
          <span className="text-sm font-semibold flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300">
            <RefreshCw className="h-4 w-4" />
            Recent Scraper Runs (Direct SSH)
          </span>
          <div className="border rounded-lg overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-800 bg-zinc-50/30 dark:bg-zinc-950/20">
            {loadingRuns && recentRuns.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading...
              </div>
            ) : recentRuns.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-sm text-zinc-500">
                No runs found
              </div>
            ) : (
              recentRuns.map(run => (
                <div key={run.id} className="flex items-center justify-between px-4 py-2.5 text-xs gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{run.job.toUpperCase()}</span>
                    <span className="text-zinc-400">{new Date(run.startedAt).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      variant={
                        run.status === 'success' ? 'default'
                          : run.status === 'failure' ? 'destructive'
                          : 'secondary'
                      }
                      className={`text-[10px] ${run.status === 'success' ? 'bg-emerald-500 text-white' : ''}`}
                    >
                      {run.status === 'success' ? '✓ Success'
                        : run.status === 'failure' ? '✗ Error'
                        : run.status === 'running' ? '⏳ Running'
                        : run.status === 'starting_vm' ? '🚀 Starting VM'
                        : run.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
